// The away seam (#403): you come back to a channel you never left, and a line shows where what arrived meanwhile starts.
// One rule for every client; the cases are shared/cases/away-seam.json, which the Swift and Kotlin ports run too. Each
// client records when it went away and the last message in view (its own lifecycle events), and asks this when it comes
// back. The seam is the open-time NEW line moved: `after` is the same "everything after this seq" the NEW line takes.

/** Away for at least this long (the tab hidden or the window out of focus, the app in the background) and back: a seam. */
export const AWAY_MS = 2 * 60_000;

/** The part of a message the rule reads. `channelId` is a thread reply's "t:<id>"; `state` is set on a send not confirmed. */
export interface AwayMessage {
  channelId: string;
  seq: number;
  authorId: string;
  deleted?: boolean;
  state?: string;
}

export interface AwaySeam {
  /** The seam goes above the first message after this seq (the NEW line's `after`). */
  after: number;
  /** New messages that are not mine. */
  count: number;
}

/** The most messages the client loads back to reach the last one seen (the open-time NEW line's limit, #202). */
export const AWAY_MAX_LOAD = 1000;

/** What both questions read: the times, the last message seen, and the channel as held now. */
export interface AwayInput {
  leftAt: number;
  backAt: number;
  seenSeq: number;
  me: string | null;
  messages: readonly AwayMessage[];
  /** Whether the room has messages older than the oldest held. Default false. */
  hasOlder?: boolean;
  /** The channel's newest seq, as the room says. Needed by `awayLoadsBack`. */
  lastSeq?: number;
}

const heldIn = (m: AwayMessage) => !m.state && !m.channelId.startsWith("t:");

/**
 * Whether the held messages reach back to the one after the last seen: the oldest of them (a deleted one too, not a
 * thread reply or a send not confirmed) is at most `seenSeq + 1`. After a reconnect the room sends only the newest page,
 * so they may start later than that.
 */
function reaches(input: AwayInput): boolean {
  let oldest = Infinity;
  for (const m of input.messages) if (heldIn(m) && m.seq < oldest) oldest = m.seq;
  return oldest <= input.seenSeq + 1;
}

/**
 * Where the seam goes, or null for none. `seenSeq` is the newest message seen when leaving: the list's end, or the last
 * row in view if scrolled up. `messages` are the channel's, as held now. The seam sits above the first message after
 * `seenSeq`, not counting a deleted one, a thread reply (it is not in the channel) or a send not confirmed yet. It counts
 * those messages that are not mine, and there is none when all of them are mine. Away under `AWAY_MS` gives none. When
 * older messages exist and the held ones do not reach back to `seenSeq`, there is none yet: the place and the count would
 * be a guess (`awayLoadsBack` says to load them).
 */
export function awaySeam(input: AwayInput): AwaySeam | null {
  if (!(input.backAt - input.leftAt >= AWAY_MS)) return null;
  if (input.hasOlder && !reaches(input)) return null;
  let first = Infinity;
  let count = 0;
  for (const m of input.messages) {
    if (m.seq <= input.seenSeq || m.deleted || !heldIn(m)) continue;
    if (m.seq < first) first = m.seq;
    if (m.authorId !== input.me) count++;
  }
  return count > 0 ? { after: first - 1, count } : null;
}

/**
 * Whether to load older messages first: away `AWAY_MS` or more, older messages exist, the channel has something newer
 * than `seenSeq` (at most `AWAY_MAX_LOAD` newer), and the held messages do not reach back to it. The client loads back
 * to `seenSeq` as it does for the open-time NEW line, then asks `awaySeam` again.
 */
export function awayLoadsBack(input: AwayInput): boolean {
  if (!(input.backAt - input.leftAt >= AWAY_MS) || !input.hasOlder) return false;
  const last = input.lastSeq ?? 0;
  return last > input.seenSeq && last - input.seenSeq <= AWAY_MAX_LOAD && !reaches(input);
}

/** The seam's words: "New since 2:41 PM · 12". `time` is when I left, written for the viewer's clock. */
export function awaySeamLabel(time: string, count: number): string {
  return `New since ${time} · ${count}`;
}
